import React from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Custom Orders table — admin-created orders that bypass the customer
 * payment / proof flow. Each row exposes Edit + Delete; the parent
 * AdminDashboard owns the modal state and the API plumbing.
 */
export const CustomOrdersTable = ({
  orders,
  loading,
  onCreate,
  onEdit,
  onDelete,
}) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
      <div>
        <h3 className="text-lg font-bold text-[#4A7C59]">Custom Orders</h3>
        <p className="text-sm text-gray-500">
          Operator-entered orders (phone-in, trade-show, internal). Skip customer payment + proof approval.
        </p>
      </div>
      <Button
        onClick={onCreate}
        data-testid="custom-orders-new-btn"
        className="bg-[#4A7C59] hover:bg-[#3D6A4A] text-white"
      >
        <Plus className="w-4 h-4 mr-1" />
        New Custom Order
      </Button>
    </div>

    {loading ? (
      <div className="px-6 py-12 text-center text-gray-500" data-testid="custom-orders-loading">
        Loading custom orders…
      </div>
    ) : orders.length === 0 ? (
      <div className="px-6 py-12 text-center text-gray-500" data-testid="custom-orders-empty">
        No custom orders yet. Click <strong>New Custom Order</strong> to enter one.
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="custom-orders-table">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Order #</th>
              <th className="px-4 py-3 text-left font-semibold">Customer</th>
              <th className="px-4 py-3 text-left font-semibold">Product</th>
              <th className="px-4 py-3 text-left font-semibold">Qty</th>
              <th className="px-4 py-3 text-right font-semibold">Total</th>
              <th className="px-4 py-3 text-right font-semibold">Per label</th>
              <th className="px-4 py-3 text-left font-semibold">Outsource</th>
              <th className="px-4 py-3 text-left font-semibold">Created</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const item = (o.items && o.items[0]) || {};
              return (
                <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`custom-orders-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{o.order_number || o.id.slice(0, 8)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{o.customer_name || '—'}</div>
                    <div className="text-xs text-gray-500">{o.customer_email || ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{item.product_type || '—'}</div>
                    <div className="text-xs text-gray-500">{item.size || ''}</div>
                  </td>
                  <td className="px-4 py-3">{item.quantity ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold">
                    {o.currency} ${Number(o.total_price || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {o.price_per_label ? `$${Number(o.price_per_label).toFixed(4)}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {item.outsource_partner ? (
                      <div>
                        <div className="text-sm">{item.outsource_partner}</div>
                        <div className="text-xs text-gray-500">${Number(item.outsource_cost || 0).toFixed(2)}</div>
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => onEdit(o)}
                      data-testid={`custom-orders-edit-${o.id}`}
                      className="text-[#4A7C59] hover:text-[#3D6A4A] mr-3"
                      title="Edit order"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(o)}
                      data-testid={`custom-orders-delete-${o.id}`}
                      className="text-red-500 hover:text-red-700"
                      title="Delete order"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default CustomOrdersTable;
